"use client"

import React, { useState, useEffect, useMemo } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import axios from "axios"
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tabs,
  Tab,
  Badge,
  Avatar,
  IconButton,
  Collapse,
  Alert,
  CircularProgress,
  Button,
  Paper,
  Pagination,
  Stack,
} from "@mui/material"
import {
  Assignment,
  RateReview,
  Feedback,
  Timeline,
  AccountTree,
  Visibility,
  Person,
  ExpandMore,
  ExpandLess,
  Email,
  Work,
  Assessment,
  FilterList,
  Refresh,
  Star,
  StarBorder,
} from "@mui/icons-material"
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const api = axios.create({
  baseURL: `${API_URL}`,
  headers: {
    "Content-Type": "application/json",
  },
})

const ViewStatus = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const [selectedEmployee, setSelectedEmployee] = useState("")
  const [selectedTab, setSelectedTab] = useState(0)
  const [employees, setEmployees] = useState([])
  const [performanceData, setPerformanceData] = useState({
    goals: [],
    reviews: [],
    feedback: [],
    improvements: [],
    succession: [],
  })
  const [loading, setLoading] = useState({
    employees: false,
    data: false,
  })
  const [expandedItems, setExpandedItems] = useState({})
  const [lastUpdated, setLastUpdated] = useState(new Date())

  // Pagination states
  const [employeeTablePage, setEmployeeTablePage] = useState(1)
  const [detailsPage, setDetailsPage] = useState(1)
  const employeesPerPage = 7
  const itemsPerPage = 5

  useEffect(() => {
    fetchEmployees()
    fetchAllPerformanceData()
  }, [])

  useEffect(() => {
    if (selectedEmployee) {
      fetchAllPerformanceData()
    }
  }, [selectedEmployee])

  // Reset pagination when tab changes
  useEffect(() => {
    setDetailsPage(1)
  }, [selectedTab])

  // Reset pagination when employee filter changes
  useEffect(() => {
    setDetailsPage(1)
  }, [selectedEmployee])

  const handleTabChange = (event, newValue) => {
    navigate(newValue)
  }

  const fetchEmployees = async () => {
    try {
      setLoading((prev) => ({ ...prev, employees: true }))
      // Try multiple endpoints to get employee data
      let employeeData = []
 const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
    const managerId = payload.employee_id;
      try {
        const goalsResponse = await api.get(`/api/goals/employees?employee_id=${managerId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
             
            }
          }
        )
        employeeData = goalsResponse.data || []
      } catch (error) {
        try {
          const reviewsResponse = await api.get(`/api/reviews/employees?employee_id=${managerId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
               
              }
            }
          )
          employeeData = reviewsResponse.data || []
        } catch (error) {
          try {
            const successionResponse = await api.get(`/api/succession-plans/employees?employee_id=${managerId}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                 
                }
              }
            )
            employeeData = successionResponse.data || []
          } catch (error) {
            //console.error("No employee endpoints available")
          }
        }
      }

      // Normalize employee data
      const normalizedEmployees = employeeData.map((emp) => ({
        employee_id: emp.employee_id || emp.id || emp._id,
        firstName: emp.firstName || emp.first_name || emp.fname || "Unknown",
        lastName: emp.lastName || emp.last_name || emp.lname || "",
        email: emp.email || "",
        department: emp.department || emp.dept || "General",
        position: emp.position || emp.designation || emp.role || "",
      }))

      setEmployees(normalizedEmployees)
    } catch (error) {
      //console.error("Error fetching employees:", error)
    } finally {
      setLoading((prev) => ({ ...prev, employees: false }))
    }
  }

  const fetchAllPerformanceData = async () => {
    try {
       const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      setLoading((prev) => ({ ...prev, data: true }))

      // Fetch data from all available endpoints
      const dataPromises = []

      dataPromises.push(
        api
          .get(`/api/goals`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          })
          .then((response) => ({
            type: "goals",
            data: response.data || [],
          }))
          .catch(() => ({
            type: "goals",
            data: [],
          })),
      )

      // Reviews
      dataPromises.push(
        api
          .get(`/api/reviews`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json"
              }
            }
          )
          .then((response) => ({ type: "reviews", data: response.data || [] }))
          .catch(() => ({ type: "reviews", data: [] })),
      )

      // Feedback (placeholder - replace with actual endpoint when available)
      dataPromises.push(
        api
          .get(`/api/feedback`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json"
              }
            }
          )
          .then((response) => ({ type: "feedback", data: response.data || [] }))
          .catch(() => ({ type: "feedback", data: [] })),
      )

      // Improvements
      dataPromises.push(
        api
          .get(`/api/improvement-plans`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json"
              }
            }
          )
          .then((response) => ({ type: "improvements", data: response.data || [] }))
          .catch(() => ({ type: "improvements", data: [] })),
      )

      // Succession
      dataPromises.push(
        api
          .get(`/api/succession-plans`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json"
              }
            }
          )
          .then((response) => ({ type: "succession", data: response.data || [] }))
          .catch(() => ({ type: "succession", data: [] })),
      )

      const results = await Promise.all(dataPromises)

      const newPerformanceData = {
        goals: [],
        reviews: [],
        feedback: [],
        improvements: [],
        succession: [],
      }

      results.forEach((result) => {
        newPerformanceData[result.type] = result.data
      })

      setPerformanceData(newPerformanceData)
      setLastUpdated(new Date())
    } catch (error) {
      //console.error("Error fetching performance data:", error)
    } finally {
      setLoading((prev) => ({ ...prev, data: false }))
    }
  }

  const filteredData = useMemo(() => {
    if (!selectedEmployee) return performanceData

    return {
      goals: performanceData.goals.filter((item) => item.employee_id === selectedEmployee),
      reviews: performanceData.reviews.filter((item) => item.employee_id === selectedEmployee),
      feedback: performanceData.feedback.filter((item) => item.employee_id === selectedEmployee),
      improvements: performanceData.improvements.filter((item) => item.employee_id === selectedEmployee),
      succession: performanceData.succession.filter((item) => item.employee_id === selectedEmployee),
    }
  }, [performanceData, selectedEmployee])

  const getEmployeeName = (employeeId) => {
    const emp = employees.find((e) => e.employee_id === employeeId)
    if (!emp) return "Unknown Employee"
    const lastName = emp.lastName && emp.lastName.trim() ? emp.lastName : ""
    return lastName ? `${emp.firstName} ${lastName}` : emp.firstName
  }

  const getEmployeeDetails = (employeeId) => {
    return employees.find((e) => e.employee_id === employeeId)
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
      case "Approved":
      case "Active":
        return "success"
      case "In Progress":
      case "Pending":
      case "Scheduled":
        return "warning"
      case "Not Started":
      case "Draft":
        return "info"
      case "Overdue":
      case "Rejected":
      case "Terminated":
        return "error"
      default:
        return "default"
    }
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "High":
      case "Critical":
        return "error"
      case "Medium":
        return "warning"
      case "Low":
        return "info"
      default:
        return "default"
    }
  }

  const getStars = (rating) => {
    const ratingNum = Number.parseInt(rating) || 0
    return [...Array(5)].map((_, i) =>
      i < ratingNum ? <Star key={i} color="primary" fontSize="small" /> : <StarBorder key={i} fontSize="small" />,
    )
  }

  const formatDate = (dateString) => {
    if (!dateString) return "No date"
    const date = new Date(dateString)
    if (isNaN(date)) return "Invalid date"
    const options = { year: "numeric", month: "short", day: "numeric" }
    return date.toLocaleDateString(undefined, options)
  }

  const calculateOverallStats = () => {
    const data = selectedEmployee ? filteredData : performanceData

    const stats = {
      totalGoals: data.goals.length,
      completedGoals: data.goals.filter((g) => g.status === "Completed").length,
      inProgressGoals: data.goals.filter((g) => g.status === "In Progress").length,
      overdueGoals: data.goals.filter((g) => g.status === "Overdue").length,
      totalReviews: data.reviews.length,
      completedReviews: data.reviews.filter((r) => r.status === "Completed").length,
      scheduledReviews: data.reviews.filter((r) => r.status === "Scheduled").length,
      totalFeedback: data.feedback.length,
      totalImprovements: data.improvements.length,
      activeImprovements: data.improvements.filter((i) => i.status === "Active").length,
      totalSuccession: data.succession.length,
      activeSuccession: data.succession.filter((s) => s.status === "Active").length,
    }

    stats.completionRate = stats.totalGoals > 0 ? Math.round((stats.completedGoals / stats.totalGoals) * 100) : 0
    stats.avgProgress =
      stats.totalGoals > 0
        ? Math.round(data.goals.reduce((sum, goal) => sum + (goal.progress || 0), 0) / stats.totalGoals)
        : 0

    return stats
  }

  const getEmployeeStats = () => {
    if (selectedEmployee) {
      return [
        {
          employee_id: selectedEmployee,
          name: getEmployeeName(selectedEmployee),
          ...calculateOverallStats(),
        },
      ]
    }

    // Calculate stats for all employees
    const employeeStats = {}

    employees.forEach((emp) => {
      const empGoals = performanceData.goals.filter((g) => g.employee_id === emp.employee_id)
      const empReviews = performanceData.reviews.filter((r) => r.employee_id === emp.employee_id)
      const empFeedback = performanceData.feedback.filter((f) => f.employee_id === emp.employee_id)
      const empImprovements = performanceData.improvements.filter((i) => i.employee_id === emp.employee_id)
      const empSuccession = performanceData.succession.filter((s) => s.employee_id === emp.employee_id)

      const totalGoals = empGoals.length
      const completedGoals = empGoals.filter((g) => g.status === "Completed").length
      const avgProgress =
        totalGoals > 0 ? Math.round(empGoals.reduce((sum, goal) => sum + (goal.progress || 0), 0) / totalGoals) : 0

      employeeStats[emp.employee_id] = {
        employee_id: emp.employee_id,
        name: `${emp.firstName} ${emp.lastName}`,
        department: emp.department,
        email: emp.email,
        totalGoals,
        completedGoals,
        inProgressGoals: empGoals.filter((g) => g.status === "In Progress").length,
        overdueGoals: empGoals.filter((g) => g.status === "Overdue").length,
        totalReviews: empReviews.length,
        completedReviews: empReviews.filter((r) => r.status === "Completed").length,
        totalFeedback: empFeedback.length,
        totalImprovements: empImprovements.length,
        totalSuccession: empSuccession.length,
        avgProgress,
        completionRate: totalGoals > 0 ? Math.round((completedGoals / totalGoals) * 100) : 0,
      }
    })

    return Object.values(employeeStats)
  }

  const toggleExpanded = (id) => {
    setExpandedItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }))
  }

  const handleRefresh = () => {
    fetchAllPerformanceData()
  }

  const stats = calculateOverallStats()
  const employeeStats = getEmployeeStats()

  // Pagination for employee table
  const paginatedEmployeeStats = useMemo(() => {
    const startIndex = (employeeTablePage - 1) * employeesPerPage
    return employeeStats.slice(startIndex, startIndex + employeesPerPage)
  }, [employeeStats, employeeTablePage])

  const totalEmployeePages = Math.ceil(employeeStats.length / employeesPerPage)

  const tabData = [
    {
      label: "Goals & Objectives",
      icon: <Assignment />,
      count: filteredData.goals.length,
      data: filteredData.goals,
    },
    {
      label: "Performance Reviews",
      icon: <RateReview />,
      count: filteredData.reviews.length,
      data: filteredData.reviews,
    },
    {
      label: "Feedback System",
      icon: <Feedback />,
      count: filteredData.feedback.length,
      data: filteredData.feedback,
    },
    {
      label: "Improvement Plans",
      icon: <Timeline />,
      count: filteredData.improvements.length,
      data: filteredData.improvements,
    },
    {
      label: "Succession Planning",
      icon: <AccountTree />,
      count: filteredData.succession.length,
      data: filteredData.succession,
    },
  ]

  // Pagination for detailed module tabs
  const getCurrentTabData = () => {
    const currentTabData = tabData[selectedTab]?.data || []
    const startIndex = (detailsPage - 1) * itemsPerPage
    return currentTabData.slice(startIndex, startIndex + itemsPerPage)
  }

  const getCurrentTabTotalPages = () => {
    const currentTabData = tabData[selectedTab]?.data || []
    return Math.ceil(currentTabData.length / itemsPerPage)
  }

  const handleEmployeeTablePageChange = (event, value) => {
    setEmployeeTablePage(value)
  }

  const handleDetailsPageChange = (event, value) => {
    setDetailsPage(value)
  }

  return (
    <Container maxWidth={false} sx={{ maxWidth: 1500, p: 3, boxShadow: 3, mx: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" gutterBottom  fontWeight="bold">
          Performance Management 
        </Typography>
      </Box>

      {/* Navigation Tabs - Same as other pages */}
      <Paper elevation={3} sx={{ mb: 4 }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={location.pathname}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              backgroundColor: "#f5f5f5",
              "& .MuiTab-root": { fontWeight: "medium", py: 2 },
            }}
          >
            <Tab icon={<Assignment />} label="Goals & Objectives" value="/performance" />
            <Tab icon={<RateReview />} label="Performance Reviews" value="/review" />
            <Tab icon={<Feedback />} label="Feedback System" value="/feedback" />
            <Tab icon={<Timeline />} label="Improvement Plans" value="/improve" />
            <Tab icon={<AccountTree />} label="Succession Planning" value="/succession" />
            <Tab icon={<Visibility />} label="View Status" value="/performancestatus" />
          </Tabs>
        </Box>
      </Paper>

      {/* Only show content when on the View Status tab */}
      {location.pathname === "/performancestatus" && (
        <>
          {/* Header with Refresh Button */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Typography variant="h5" sx={{ display: "flex", alignItems: "center", fontWeight: "bold" }}>
              <Assessment sx={{ mr: 1 }} />
              Performance Status Overview
            </Typography>
            <Button variant="outlined" startIcon={<Refresh />} onClick={handleRefresh} disabled={loading.data}>
              {loading.data ? "Refreshing..." : "Refresh Data"}
            </Button>
          </Box>

          {/* Employee Filter */}
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="h6" sx={{ display: "flex", alignItems: "center" }}>
                  <FilterList sx={{ mr: 1 }} />
                  Employee Filter
                </Typography>

                <FormControl sx={{ minWidth: 300 }}>
                  <InputLabel>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Person sx={{ mr: 0.5, fontSize: 16 }} />
                      Select Employee
                    </Box>
                  </InputLabel>
                  <Select
                    value={selectedEmployee}
                    onChange={(e) => setSelectedEmployee(e.target.value)}
                    label="Select Employee"
                    disabled={loading.employees}
                  >
                    <MenuItem value="">
                      <em>All Employees</em>
                    </MenuItem>
                    {employees.map((employee) => (
                      <MenuItem key={employee.employee_id} value={employee.employee_id}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Avatar sx={{ mr: 1, width: 24, height: 24, fontSize: 12 }}>
                            {employee.firstName[0]}
                            {employee.lastName[0]}
                          </Avatar>
                          {`${employee.firstName} ${employee.lastName}`} 
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {selectedEmployee && (
                <Box sx={{ mt: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                  {(() => {
                    const empDetails = getEmployeeDetails(selectedEmployee)
                    return empDetails ? (
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Avatar sx={{ mr: 1 }}>
                              {empDetails.firstName[0]}
                              {empDetails.lastName[0]}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2">
                                {empDetails.firstName} {empDetails.lastName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {empDetails.position}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Work sx={{ mr: 1, fontSize: 16 }} />
                            <Typography variant="body2">{empDetails.department}</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Email sx={{ mr: 1, fontSize: 16 }} />
                            <Typography variant="body2">{empDetails.email}</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Person sx={{ mr: 1,ml:10, fontSize: 16 }} />
                            <Typography variant="body2">ID: {empDetails.employee_id}</Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    ) : null
                  })()}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Summary Statistics Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Assignment sx={{ fontSize: 40, color: "primary.main", mb: 1 }} />
                  <Typography variant="h4" fontWeight="bold">
                    {stats.totalGoals}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Goals
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    {stats.completedGoals} Completed
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: "center" }}>
                  <RateReview sx={{ fontSize: 40, color: "warning.main", mb: 1 }} />
                  <Typography variant="h4" fontWeight="bold">
                    {stats.totalReviews}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Reviews
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    {stats.completedReviews || 0} Completed
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Feedback sx={{ fontSize: 40, color: "info.main", mb: 1 }} />
                  <Typography variant="h4" fontWeight="bold">
                    {stats.totalFeedback}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Feedback Items
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    All Modules
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Timeline sx={{ fontSize: 40, color: "secondary.main", mb: 1 }} />
                  <Typography variant="h4" fontWeight="bold">
                    {stats.totalImprovements}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Improvement Plans
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    {stats.activeImprovements || 0} Active
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: "center" }}>
                  <AccountTree sx={{ fontSize: 40, color: "success.main", mb: 1 }} />
                  <Typography variant="h4" fontWeight="bold">
                    {stats.totalSuccession}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Succession Plans
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    {stats.activeSuccession || 0} Active
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Employee Statistics Table with Pagination */}
          <Card elevation={3} sx={{ mb: 4 }}>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" sx={{ display: "flex", alignItems: "center" }}>
                  <Assessment sx={{ mr: 1 }} />
                  {selectedEmployee ? "Employee Details" : "Employee Performance Overview"}
                </Typography>
                {!selectedEmployee && (
                  <Typography variant="body2" color="text.secondary">
                    Showing {(employeeTablePage - 1) * employeesPerPage + 1}-
                    {Math.min(employeeTablePage * employeesPerPage, employeeStats.length)} of {employeeStats.length}{" "}
                    employees
                  </Typography>
                )}
              </Box>

              {loading.data ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                          <TableCell>
                            <strong>Employee</strong>
                          </TableCell>
                          {!selectedEmployee && (
                            <TableCell>
                              <strong>Department</strong>
                            </TableCell>
                          )}
                          <TableCell align="center">
                            <strong>Goals</strong>
                          </TableCell>
                          <TableCell align="center">
                            <strong>Reviews</strong>
                          </TableCell>
                          <TableCell align="center">
                            <strong>Feedback</strong>
                          </TableCell>
                          <TableCell align="center">
                            <strong>Improvements</strong>
                          </TableCell>
                          <TableCell align="center">
                            <strong>Succession</strong>
                          </TableCell>
                          <TableCell align="center">
                            <strong>Progress</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedEmployeeStats.map((emp) => (
                          <TableRow key={emp.employee_id} hover>
                            <TableCell>
                              <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Avatar sx={{ mr: 2, bgcolor: "primary.main" }}>
                                  {emp.name
                                    .split(" ")
                                    .map((n) => n[0])
                                    .join("")}
                                </Avatar>
                                <Box>
                                  <Typography variant="subtitle2">{emp.name}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {emp.email}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            {!selectedEmployee && <TableCell>{emp.department}</TableCell>}
                            <TableCell align="center">
                              <Chip label={emp.totalGoals} color="primary" variant="outlined" />
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={emp.totalReviews} color="warning" variant="outlined" />
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={emp.totalFeedback} color="info" variant="outlined" />
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={emp.totalImprovements} color="secondary" variant="outlined" />
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={emp.totalSuccession} color="success" variant="outlined" />
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={emp.avgProgress}
                                  sx={{ width: 60, mr: 1 }}
                                />
                                <Typography variant="body2">{emp.avgProgress}%</Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Employee Table Pagination */}
                  {!selectedEmployee && totalEmployeePages > 1 && (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                      <Stack spacing={2}>
                        <Pagination
                          count={totalEmployeePages}
                          page={employeeTablePage}
                          onChange={handleEmployeeTablePageChange}
                          color="primary"
                          size="large"
                          showFirstButton
                          showLastButton
                        />
                      </Stack>
                    </Box>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Detailed Module Tabs with Pagination */}
          <Card elevation={3}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={selectedTab}
                onChange={(e, newValue) => setSelectedTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
              >
                {tabData.map((tab, index) => (
                  <Tab
                    key={index}
                    icon={tab.icon}
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {tab.label}
                        <Badge badgeContent={tab.count} color="primary" />
                      </Box>
                    }
                  />
                ))}
              </Tabs>
            </Box>

            <CardContent>
              {/* Pagination Info */}
              {tabData[selectedTab]?.data.length > 0 && (
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {tabData[selectedTab]?.label} Status
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Showing {(detailsPage - 1) * itemsPerPage + 1}-
                    {Math.min(detailsPage * itemsPerPage, tabData[selectedTab]?.data.length || 0)} of{" "}
                    {tabData[selectedTab]?.data.length || 0} items
                  </Typography>
                </Box>
              )}

              {/* Goals & Objectives Tab */}
              {selectedTab === 0 && (
                <Box>
                  {!tabData[selectedTab]?.data.length && (
                    <Typography variant="h6" gutterBottom>
                      Goals & Objectives Status
                    </Typography>
                  )}

                  {getCurrentTabData().length === 0 && filteredData.goals.length === 0 ? (
                    <Alert severity="info">
                      {selectedEmployee
                        ? `No goals found for ${getEmployeeName(selectedEmployee)}`
                        : "No goals available in the system"}
                    </Alert>
                  ) : (
                    <List>
                      {getCurrentTabData().map((goal) => (
                        <React.Fragment key={goal.id}>
                          <ListItem
                            sx={{
                              border: "1px solid #e0e0e0",
                              borderRadius: 1,
                              mb: 1,
                              bgcolor: "#fafafa",
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                  <Typography variant="subtitle1" fontWeight="medium">
                                    {goal.title}
                                  </Typography>
                                  <Box sx={{ display: "flex", gap: 1 }}>
                                    <Chip label={goal.status} color={getStatusColor(goal.status)} size="small" />
                                    <Chip label={goal.priority} color={getPriorityColor(goal.priority)} size="small" />
                                  </Box>
                                </Box>
                              }
                              secondary={
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    {!selectedEmployee && `Employee: ${getEmployeeName(goal.employee_id)} | `}
                                    Due: {formatDate(goal.dueDate)} | Progress: {goal.progress || 0}%
                                  </Typography>
                                  <LinearProgress
                                    variant="determinate"
                                    value={goal.progress || 0}
                                    sx={{ mt: 1, height: 6, borderRadius: 3 }}
                                  />
                                  {goal.progressUpdates && goal.progressUpdates.length > 0 && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ mt: 0.5, display: "block" }}
                                    >
                                      Last update:{" "}
                                      {formatDate(goal.progressUpdates[goal.progressUpdates.length - 1].date)}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                            <ListItemSecondaryAction>
                              <IconButton onClick={() => toggleExpanded(`goal-${goal.id}`)} size="small">
                                {expandedItems[`goal-${goal.id}`] ? <ExpandLess /> : <ExpandMore />}
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>

                          <Collapse in={expandedItems[`goal-${goal.id}`]} timeout="auto" unmountOnExit>
                            <Box
                              sx={{
                                ml: 2,
                                mr: 2,
                                mb: 2,
                                p: 2,
                                bgcolor: "white",
                                border: "1px solid #e0e0e0",
                                borderRadius: 1,
                              }}
                            >
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="body2" paragraph>
                                    <strong>Description:</strong> {goal.description || "No description provided"}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Metrics:</strong> {goal.metrics || "No metrics defined"}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="body2">
                                    <strong>Team:</strong> {goal.team || "Not specified"}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Created:</strong> {formatDate(goal.createdAt)}
                                  </Typography>
                                </Grid>
                              </Grid>

                              {goal.progressUpdates && goal.progressUpdates.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Progress Update History:
                                  </Typography>
                                  <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                                    {goal.progressUpdates.map((update, index) => (
                                      <Paper
                                        key={index}
                                        sx={{
                                          p: 2,
                                          mb: 1,
                                          borderLeft: 4,
                                          borderColor:
                                            getStatusColor(update.status) === "error"
                                              ? "#f44336"
                                              : getStatusColor(update.status) === "warning"
                                                ? "#ff9800"
                                                : "#2196f3",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            mb: 1,
                                          }}
                                        >
                                          <Typography variant="caption" color="text.secondary">
                                            {new Date(update.date).toLocaleDateString()} -{" "}
                                            {new Date(update.date).toLocaleTimeString()}
                                          </Typography>
                                          <Box sx={{ display: "flex", gap: 1 }}>
                                            <Chip
                                              label={update.status}
                                              size="small"
                                              color={getStatusColor(update.status)}
                                            />
                                            <Chip
                                              label={`${update.progress}%`}
                                              size="small"
                                              color={
                                                update.progress >= 75
                                                  ? "success"
                                                  : update.progress >= 50
                                                    ? "warning"
                                                    : "error"
                                              }
                                            />
                                          </Box>
                                        </Box>
                                        {update.note && (
                                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                                            {update.note}
                                          </Typography>
                                        )}
                                      </Paper>
                                    ))}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          </Collapse>
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </Box>
              )}

              {/* Performance Reviews Tab */}
              {selectedTab === 1 && (
                <Box>
                  {!tabData[selectedTab]?.data.length && (
                    <Typography variant="h6" gutterBottom>
                      Performance Reviews Status
                    </Typography>
                  )}

                  {getCurrentTabData().length === 0 && filteredData.reviews.length === 0 ? (
                    <Alert severity="info">
                      {selectedEmployee
                        ? `No reviews found for ${getEmployeeName(selectedEmployee)}`
                        : "No reviews available in the system"}
                    </Alert>
                  ) : (
                    <List>
                      {getCurrentTabData().map((review) => (
                        <React.Fragment key={review.id}>
                          <ListItem
                            sx={{
                              border: "1px solid #e0e0e0",
                              borderRadius: 1,
                              mb: 1,
                              bgcolor: "#fafafa",
                              borderLeft: "4px solid",
                              borderLeftColor:
                                getStatusColor(review.status) === "error"
                                  ? "#f44336"
                                  : getStatusColor(review.status) === "warning"
                                    ? "#ff9800"
                                    : "#2196f3",
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                  <Typography variant="subtitle1" fontWeight="medium">
                                    {review.type} Review - {getEmployeeName(review.employee_id)}
                                  </Typography>
                                  <Chip
                                    label={review.status || "Scheduled"}
                                    color={getStatusColor(review.status)}
                                    size="small"
                                  />
                                </Box>
                              }
                              secondary={
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    Date: {formatDate(review.reviewDate)} | Reviewer:{" "}
                                    {review.reviewer || "Not assigned"}
                                  </Typography>
                                  {review.rating && (
                                    <Box sx={{ mt: 1, display: "flex", alignItems: "center" }}>
                                      <Typography variant="body2" sx={{ mr: 1 }}>
                                        Rating:
                                      </Typography>
                                      {getStars(review.rating)}
                                    </Box>
                                  )}
                                  {review.progress !== undefined && (
                                    <Box sx={{ mt: 1 }}>
                                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                        <Typography variant="caption">Progress</Typography>
                                        <Typography variant="caption">{review.progress || 0}%</Typography>
                                      </Box>
                                      <LinearProgress
                                        variant="determinate"
                                        value={review.progress || 0}
                                        sx={{ height: 6, borderRadius: 3 }}
                                      />
                                    </Box>
                                  )}
                                  {review.comments && (
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                      <strong>Comments:</strong> {review.comments}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                            <ListItemSecondaryAction>
                              <IconButton onClick={() => toggleExpanded(`review-${review.id}`)} size="small">
                                {expandedItems[`review-${review.id}`] ? <ExpandLess /> : <ExpandMore />}
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>

                          <Collapse in={expandedItems[`review-${review.id}`]} timeout="auto" unmountOnExit>
                            <Box
                              sx={{
                                ml: 2,
                                mr: 2,
                                mb: 2,
                                p: 2,
                                bgcolor: "white",
                                border: "1px solid #e0e0e0",
                                borderRadius: 1,
                              }}
                            >
                              {review.progressUpdates && review.progressUpdates.length > 0 && (
                                <Box>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Review Progress History:
                                  </Typography>
                                  <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                                    {review.progressUpdates.map((update, index) => (
                                      <Paper
                                        key={index}
                                        sx={{
                                          p: 2,
                                          mb: 1,
                                          borderLeft: 4,
                                          borderColor:
                                            getStatusColor(update.status) === "error"
                                              ? "#f44336"
                                              : getStatusColor(update.status) === "warning"
                                                ? "#ff9800"
                                                : "#2196f3",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            mb: 1,
                                          }}
                                        >
                                          <Typography variant="caption" color="text.secondary">
                                            {new Date(update.date).toLocaleDateString()} -{" "}
                                            {new Date(update.date).toLocaleTimeString()}
                                          </Typography>
                                          <Box sx={{ display: "flex", gap: 1 }}>
                                            <Chip
                                              label={update.status}
                                              size="small"
                                              color={getStatusColor(update.status)}
                                            />
                                            <Chip
                                              label={`${update.progress}%`}
                                              size="small"
                                              color={
                                                update.progress >= 75
                                                  ? "success"
                                                  : update.progress >= 50
                                                    ? "warning"
                                                    : "error"
                                              }
                                            />
                                          </Box>
                                        </Box>
                                        {update.note && (
                                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                                            {update.note}
                                          </Typography>
                                        )}
                                      </Paper>
                                    ))}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          </Collapse>
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </Box>
              )}

              {/* Feedback System Tab */}
              {selectedTab === 2 && (
                <Box>
                  {!tabData[selectedTab]?.data.length && (
                    <Typography variant="h6" gutterBottom>
                      Feedback System Status
                    </Typography>
                  )}
                  {getCurrentTabData().length === 0 && filteredData.feedback.length === 0 ? (
                    <Alert severity="info">
                      {selectedEmployee
                        ? `No feedback found for ${getEmployeeName(selectedEmployee)}`
                        : "No feedback available in the system"}
                    </Alert>
                  ) : (
                    <List>
                      {getCurrentTabData().map((item) => (
                        <React.Fragment key={item.id}>
                          <ListItem
                            sx={{
                              border: "1px solid #e0e0e0",
                              borderRadius: 1,
                              mb: 1,
                              bgcolor: "#fafafa",
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                  <Typography variant="subtitle1" fontWeight="medium">
                                    {item.anonymous ? "Anonymous" : item.type} Feedback for{" "}
                                    {getEmployeeName(item.employee_id)}
                                  </Typography>
                                  <Chip label={item.type} color="info" size="small" />
                                </Box>
                              }
                              secondary={
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    Date: {formatDate(item.date)}
                                  </Typography>
                                  <Box sx={{ mt: 1, display: "flex", alignItems: "center" }}>
                                    <Typography variant="body2" sx={{ mr: 1 }}>
                                      Rating:
                                    </Typography>
                                    {getStars(item.rating)}
                                  </Box>
                                  <Typography variant="body2" paragraph sx={{ mt: 1 }}>
                                    <strong>Comment:</strong> {item.comment}
                                  </Typography>
                                </Box>
                              }
                            />
                            <ListItemSecondaryAction>
                              {item.anonymous && <Chip label="Anonymous" color="secondary" size="small" />}
                            </ListItemSecondaryAction>
                          </ListItem>
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </Box>
              )}

              {/* Improvement Plans Tab */}
              {selectedTab === 3 && (
                <Box>
                  {!tabData[selectedTab]?.data.length && (
                    <Typography variant="h6" gutterBottom>
                      Improvement Plans Status
                    </Typography>
                  )}

                  {getCurrentTabData().length === 0 && filteredData.improvements.length === 0 ? (
                    <Alert severity="info">
                      {selectedEmployee
                        ? `No improvement plans found for ${getEmployeeName(selectedEmployee)}`
                        : "No improvement plans available in the system"}
                    </Alert>
                  ) : (
                    <List>
                      {getCurrentTabData().map((plan) => (
                        <React.Fragment key={plan.id}>
                          <ListItem
                            sx={{
                              border: "1px solid #e0e0e0",
                              borderRadius: 1,
                              mb: 1,
                              bgcolor: "#fafafa",
                              borderLeft: "4px solid",
                              borderLeftColor:
                                getStatusColor(plan.status) === "error"
                                  ? "#f44336"
                                  : getStatusColor(plan.status) === "warning"
                                    ? "#ff9800"
                                    : "#2196f3",
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                  <Typography variant="subtitle1" fontWeight="medium">
                                    Improvement Plan - {getEmployeeName(plan.employee_id)}
                                  </Typography>
                                  <Chip label={plan.status} color={getStatusColor(plan.status)} size="small" />
                                </Box>
                              }
                              secondary={
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    Period: {formatDate(plan.startDate)} - {formatDate(plan.endDate)}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    <strong>Reason:</strong> {plan.reason}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    <strong>Meeting Frequency:</strong> {plan.meetingFrequency}
                                  </Typography>
                                  {plan.progress !== undefined && (
                                    <Box sx={{ mt: 1 }}>
                                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                        <Typography variant="caption">Progress</Typography>
                                        <Typography variant="caption">{plan.progress || 0}%</Typography>
                                      </Box>
                                      <LinearProgress
                                        variant="determinate"
                                        value={plan.progress || 0}
                                        sx={{ height: 6, borderRadius: 3 }}
                                      />
                                    </Box>
                                  )}
                                </Box>
                              }
                            />
                            <ListItemSecondaryAction>
                              <IconButton onClick={() => toggleExpanded(`improvement-${plan.id}`)} size="small">
                                {expandedItems[`improvement-${plan.id}`] ? <ExpandLess /> : <ExpandMore />}
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>

                          <Collapse in={expandedItems[`improvement-${plan.id}`]} timeout="auto" unmountOnExit>
                            <Box
                              sx={{
                                ml: 2,
                                mr: 2,
                                mb: 2,
                                p: 2,
                                bgcolor: "white",
                                border: "1px solid #e0e0e0",
                                borderRadius: 1,
                              }}
                            >
                              <Grid container spacing={2}>
                                <Grid item xs={12}>
                                  <Typography variant="body2" paragraph>
                                    <strong>Objectives:</strong> {plan.objectives}
                                  </Typography>
                                  {plan.milestones && (
                                    <Typography variant="body2" paragraph>
                                      <strong>Key Milestones:</strong> {plan.milestones}
                                    </Typography>
                                  )}
                                </Grid>
                              </Grid>

                              {plan.progressUpdates && plan.progressUpdates.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Improvement Progress History:
                                  </Typography>
                                  <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                                    {plan.progressUpdates.map((update, index) => (
                                      <Paper
                                        key={index}
                                        sx={{
                                          p: 2,
                                          mb: 1,
                                          borderLeft: 4,
                                          borderColor:
                                            getStatusColor(update.status) === "error"
                                              ? "#f44336"
                                              : getStatusColor(update.status) === "warning"
                                                ? "#ff9800"
                                                : "#2196f3",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            mb: 1,
                                          }}
                                        >
                                          <Typography variant="caption" color="text.secondary">
                                            {new Date(update.date).toLocaleDateString()} -{" "}
                                            {new Date(update.date).toLocaleTimeString()}
                                          </Typography>
                                          <Box sx={{ display: "flex", gap: 1 }}>
                                            <Chip
                                              label={update.status}
                                              size="small"
                                              color={getStatusColor(update.status)}
                                            />
                                            <Chip
                                              label={`${update.progress}%`}
                                              size="small"
                                              color={
                                                update.progress >= 75
                                                  ? "success"
                                                  : update.progress >= 50
                                                    ? "warning"
                                                    : "error"
                                              }
                                            />
                                          </Box>
                                        </Box>
                                        {update.taskCompletion && (
                                          <Typography variant="body2" sx={{ mt: 1 }}>
                                            <strong>Tasks Completed:</strong> {update.taskCompletion}
                                          </Typography>
                                        )}
                                        {update.note && (
                                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                                            {update.note}
                                          </Typography>
                                        )}
                                      </Paper>
                                    ))}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          </Collapse>
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </Box>
              )}

              {/* Succession Planning Tab */}
              {selectedTab === 4 && (
                <Box>
                  {!tabData[selectedTab]?.data.length && (
                    <Typography variant="h6" gutterBottom>
                      Succession Planning Status
                    </Typography>
                  )}

                  {getCurrentTabData().length === 0 && filteredData.succession.length === 0 ? (
                    <Alert severity="info">
                      {selectedEmployee
                        ? `No succession plans found for ${getEmployeeName(selectedEmployee)}`
                        : "No succession plans available in the system"}
                    </Alert>
                  ) : (
                    <List>
                      {getCurrentTabData().map((plan) => (
                        <ListItem
                          key={plan.id}
                          sx={{
                            border: "1px solid #e0e0e0",
                            borderRadius: 1,
                            mb: 1,
                            bgcolor: "#fafafa",
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography variant="subtitle1" fontWeight="medium">
                                {plan.position} - {getEmployeeName(plan.employee_id)}
                              </Typography>
                            }
                            secondary={
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                  Readiness: {plan.readinessLevel} | Timeline: {formatDate(plan.timeline)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Successors: {plan.potentialSuccessors || "None specified"}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              )}

              {/* Details Pagination */}
              {getCurrentTabTotalPages() > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                  <Stack spacing={2}>
                    <Pagination
                      count={getCurrentTabTotalPages()}
                      page={detailsPage}
                      onChange={handleDetailsPageChange}
                      color="primary"
                      size="medium"
                      showFirstButton
                      showLastButton
                    />
                  </Stack>
                </Box>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Container>
  )
}

export default ViewStatus
